import { CHAT_ATTACHMENT_LIMITS } from '../chatUpload';

/**
 * The pure half of the image editor: what an edit *is*, how a set of edits is
 * painted onto a canvas, and how the result becomes a file again.
 *
 * Everything is expressed in IMAGE pixels - the natural coordinates of the
 * picture being edited - never in screen pixels. The component maps pointer
 * positions into this space once, on the way in, and the same render function
 * then draws both the on-screen preview (through a scaled transform) and the
 * full-resolution export. That is what keeps the two pixel-identical: there is
 * no second drawing path to drift.
 */

export type EditorPoint = { x: number; y: number };

export type EditorStroke = {
  id: number;
  color: string;
  /** Line width, in image pixels. */
  size: number;
  points: EditorPoint[];
};

export type EditorText = {
  id: number;
  value: string;
  /** Centre of the text, in image pixels. */
  x: number;
  y: number;
  color: string;
  /** Font size, in image pixels. */
  size: number;
};

export type EditorCrop = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export type EditorState = {
  strokes: EditorStroke[];
  texts: EditorText[];
  /** Null means the full image. */
  crop: EditorCrop | null;
};

export const EMPTY_EDITOR_STATE: EditorState = {
  strokes: [],
  texts: [],
  crop: null,
};

export const isPristine = (state: EditorState) =>
  state.strokes.length === 0 && state.texts.length === 0 && state.crop === null;

/* -------------------------------------------------------------------------- */
/* Which files the editor takes                                               */
/* -------------------------------------------------------------------------- */

/**
 * The editor draws through a canvas, so it can only take what a canvas can
 * decode and re-encode. The two deliberate exclusions from the accepted image
 * list: gif, because flattening one loses the animation, and heic/heif, which
 * Chrome's canvas cannot decode at all. Both still send fine - they just skip
 * the editor and go through the plain staged flow.
 */
const EDITABLE_IMAGE_MIMES = ['image/jpeg', 'image/png', 'image/webp'];

export const isEditableImage = (file: File) =>
  EDITABLE_IMAGE_MIMES.includes(file.type);

/* -------------------------------------------------------------------------- */
/* The colour slider                                                          */
/* -------------------------------------------------------------------------- */

/**
 * One track, two zones: the first stretch runs the full hue circle, the tail
 * ramps white to black - because annotations need white and black at least as
 * often as they need a hue, and a pure hue slider has neither.
 */
const HUE_PORTION = 0.78;

export const DEFAULT_COLOR_T = 0;

export const sliderColor = (t: number): string => {
  const clamped = Math.min(Math.max(t, 0), 1);
  if (clamped <= HUE_PORTION) {
    return `hsl(${Math.round((clamped / HUE_PORTION) * 360)}, 100%, 50%)`;
  }
  const lightness = Math.round(
    (1 - (clamped - HUE_PORTION) / (1 - HUE_PORTION)) * 100
  );
  return `hsl(0, 0%, ${lightness}%)`;
};

/**
 * The track's background, generated from `sliderColor` itself so the paint
 * under the thumb and the colour it picks can never disagree.
 */
export const SLIDER_TRACK_GRADIENT = (() => {
  const stops: string[] = [];
  for (let i = 0; i <= 12; i += 1) {
    const t = (i / 12) * HUE_PORTION;
    stops.push(`${sliderColor(t)} ${(t * 100).toFixed(1)}%`);
  }
  // A hard edge into the grayscale tail - a blend would put a band of pale
  // pink between red and white that the colour function cannot produce.
  stops.push(`#FFFFFF ${(HUE_PORTION * 100).toFixed(1)}%`, '#000000 100%');
  return `linear-gradient(to right, ${stops.join(', ')})`;
})();

/* -------------------------------------------------------------------------- */
/* Sizes                                                                      */
/* -------------------------------------------------------------------------- */

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

/**
 * The stroke width one notch of the slider means nothing on its own - what it
 * scales is a width derived from the picture.
 *
 * Proportional, not fixed: a 12MP photo and a 400px screenshot should both come
 * out with a stroke that reads as "marker", and a fixed pixel width would be
 * invisible on one and a paint roller on the other. The slider then moves that
 * baseline up and down, so the same position on the bar means the same
 * thickness RELATIVE TO THE PICTURE whatever was picked.
 */
const baseBrushSize = (image: HTMLImageElement) =>
  clamp(
    Math.round(Math.max(image.naturalWidth, image.naturalHeight) / 120),
    4,
    40
  );

const BRUSH_MIN_FACTOR = 0.35;
const BRUSH_MAX_FACTOR = 3;

/** Mid-track, which geometrically works out at the baseline itself (~1x). */
export const DEFAULT_BRUSH_T = 0.5;

/**
 * Geometric rather than linear across the track: thickness is judged by ratio,
 * so a linear ramp spends most of its travel on fat strokes that all look the
 * same and crams every useful thin one into the first centimetre.
 */
export const brushSizeFor = (
  image: HTMLImageElement,
  t: number = DEFAULT_BRUSH_T
) => {
  const clamped = clamp(t, 0, 1);
  const factor =
    BRUSH_MIN_FACTOR * (BRUSH_MAX_FACTOR / BRUSH_MIN_FACTOR) ** clamped;
  return Math.max(1, Math.round(baseBrushSize(image) * factor));
};

export const textSizeFor = (image: HTMLImageElement) =>
  clamp(
    Math.round(Math.max(image.naturalWidth, image.naturalHeight) / 16),
    24,
    144
  );

/** The one font both the canvas and the DOM edit-input must agree on. */
export const textFont = (size: number) =>
  `600 ${size}px 'SF Pro Display', sans-serif`;

/* -------------------------------------------------------------------------- */
/* Rendering                                                                  */
/* -------------------------------------------------------------------------- */

const traceStroke = (ctx: CanvasRenderingContext2D, stroke: EditorStroke) => {
  const { points } = stroke;
  if (points.length === 0) return;

  // A click without a drag still leaves a mark - a dot the brush's width.
  if (points.length === 1) {
    ctx.beginPath();
    ctx.fillStyle = stroke.color;
    ctx.arc(points[0].x, points[0].y, stroke.size / 2, 0, Math.PI * 2);
    ctx.fill();
    return;
  }

  ctx.beginPath();
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.strokeStyle = stroke.color;
  ctx.lineWidth = stroke.size;
  ctx.moveTo(points[0].x, points[0].y);
  /*
   * Through midpoints, with the sampled point as the control: the classic
   * smoothing that turns a polyline of pointer samples into a curve, cheap
   * enough to re-run on every frame of the drag.
   */
  for (let i = 1; i < points.length - 1; i += 1) {
    const midX = (points[i].x + points[i + 1].x) / 2;
    const midY = (points[i].y + points[i + 1].y) / 2;
    ctx.quadraticCurveTo(points[i].x, points[i].y, midX, midY);
  }
  const last = points[points.length - 1];
  ctx.lineTo(last.x, last.y);
  ctx.stroke();
};

const drawText = (ctx: CanvasRenderingContext2D, text: EditorText) => {
  ctx.font = textFont(text.size);
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = text.color;
  ctx.fillText(text.value, text.x, text.y);
};

/**
 * Paints the image and every committed edit, in image pixels; the caller's
 * transform decides where that lands on screen. `skipTextId` hides the one
 * text currently being typed into - its DOM input is standing in for it.
 */
export const renderEditorState = (
  ctx: CanvasRenderingContext2D,
  image: HTMLImageElement,
  state: EditorState,
  skipTextId?: number | null
) => {
  ctx.drawImage(image, 0, 0);
  state.strokes.forEach((stroke) => traceStroke(ctx, stroke));
  state.texts.forEach((text) => {
    if (text.id !== skipTextId) drawText(ctx, text);
  });
};

/** Exposed for live drawing: the in-progress stroke is not in the state yet. */
export const renderStroke = traceStroke;

/* -------------------------------------------------------------------------- */
/* Text measuring / hit testing                                               */
/* -------------------------------------------------------------------------- */

let measureContext: CanvasRenderingContext2D | null = null;

export const measureTextWidth = (value: string, size: number) => {
  if (!measureContext) {
    measureContext = document.createElement('canvas').getContext('2d');
  }
  if (!measureContext) return value.length * size * 0.6;
  measureContext.font = textFont(size);
  return measureContext.measureText(value).width;
};

/** Topmost text under the point, or null. Both in image pixels. */
export const hitTestText = (
  texts: EditorText[],
  point: EditorPoint
): EditorText | null => {
  for (let i = texts.length - 1; i >= 0; i -= 1) {
    const text = texts[i];
    const halfWidth = measureTextWidth(text.value, text.size) / 2 + text.size * 0.2;
    const halfHeight = text.size * 0.7;
    if (
      Math.abs(point.x - text.x) <= halfWidth &&
      Math.abs(point.y - text.y) <= halfHeight
    ) {
      return text;
    }
  }
  return null;
};

/* -------------------------------------------------------------------------- */
/* Crop helpers                                                               */
/* -------------------------------------------------------------------------- */

/** Integer pixels, clamped inside the image, never smaller than 1x1. */
export const normalizeCrop = (
  crop: EditorCrop,
  imageWidth: number,
  imageHeight: number
): EditorCrop => {
  const x = clamp(Math.round(crop.x), 0, imageWidth - 1);
  const y = clamp(Math.round(crop.y), 0, imageHeight - 1);
  return {
    x,
    y,
    width: clamp(Math.round(crop.width), 1, imageWidth - x),
    height: clamp(Math.round(crop.height), 1, imageHeight - y),
  };
};

/** A rect that covers (as good as) the whole image is not a crop at all. */
export const isFullCrop = (
  crop: EditorCrop,
  imageWidth: number,
  imageHeight: number
) =>
  crop.x <= 1 &&
  crop.y <= 1 &&
  crop.x + crop.width >= imageWidth - 1 &&
  crop.y + crop.height >= imageHeight - 1;

/* -------------------------------------------------------------------------- */
/* Loading and exporting                                                      */
/* -------------------------------------------------------------------------- */

/** Decodes the picked file; the caller owns (and must revoke) the URL. */
export const loadImageElement = (
  file: File
): Promise<{ image: HTMLImageElement; url: string }> =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => resolve({ image, url });
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error(`Couldn't decode ${file.name}.`));
    };
    image.src = url;
  });

const EXTENSION_FOR_MIME: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
};

const renamedForMime = (name: string, mime: string) => {
  const extension = EXTENSION_FOR_MIME[mime];
  if (!extension) return name;
  const base = name.replace(/\.[^.]+$/, '');
  return `${base || 'image'}.${extension}`;
};

const canvasToBlob = (canvas: HTMLCanvasElement, mime: string): Promise<Blob> =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) =>
        blob ? resolve(blob) : reject(new Error("Couldn't encode the image.")),
      mime,
      0.92
    );
  });

/**
 * Rasterises the edits into a new file, at the image's full resolution.
 *
 * An untouched image returns the ORIGINAL file object - no decode/re-encode
 * round trip, no generation loss, byte-identical to what was picked.
 *
 * The browser is free to ignore a `toBlob` mime it cannot encode (Safari does
 * this for webp) and hand back a png instead, so the returned blob's own type
 * is what names the file - never the type that was asked for. And if that png
 * of a big photo lands over the upload limit, one jpeg re-encode brings it
 * under rather than failing a validation the user cannot do anything about.
 */
export const exportEditedImage = async (
  image: HTMLImageElement,
  state: EditorState,
  original: File
): Promise<File> => {
  if (isPristine(state)) return original;

  const full = document.createElement('canvas');
  full.width = image.naturalWidth;
  full.height = image.naturalHeight;
  const ctx = full.getContext('2d');
  if (!ctx) throw new Error("Couldn't encode the image.");
  renderEditorState(ctx, image, state);

  let source = full;
  if (state.crop) {
    const crop = normalizeCrop(state.crop, full.width, full.height);
    const cut = document.createElement('canvas');
    cut.width = crop.width;
    cut.height = crop.height;
    const cutCtx = cut.getContext('2d');
    if (!cutCtx) throw new Error("Couldn't encode the image.");
    cutCtx.drawImage(full, -crop.x, -crop.y);
    source = cut;
  }

  const preferred = EXTENSION_FOR_MIME[original.type]
    ? original.type
    : 'image/jpeg';
  let blob = await canvasToBlob(source, preferred);

  if (
    blob.size > CHAT_ATTACHMENT_LIMITS.image.maxSize &&
    blob.type !== 'image/jpeg'
  ) {
    blob = await canvasToBlob(source, 'image/jpeg');
  }

  return new File([blob], renamedForMime(original.name, blob.type), {
    type: blob.type,
  });
};
